/**
 * TimelogRefreshScheduler — Background timelog refresh (T2.5-T2.9)
 * 
 * Responsibilities:
 * - Start/stop periodic refresh timer (5-minute interval)
 * - Fetch incremental timelogs since lastFetchedAt
 * - Invalidate React Query cache on new data
 * - Clean up resources on unmount
 */

import type { QueryClient } from '@tanstack/react-query';
import { CompositeTimelogRepository } from '../repositories/CompositeTimelogRepository';
import { LocalTimelogRepository } from '../repositories/LocalTimelogRepository';
import { db } from '@/shared/db/database';

/** Number of timelogs to retain when pruning (≈30 days × 50 events/day). */
const PRUNE_KEEP_COUNT = 30 * 50;

/** Storage-usage ratio above which oldest timelogs are pruned. */
const STORAGE_PRUNE_THRESHOLD = 0.9;

export class TimelogRefreshScheduler {
  private static instance: TimelogRefreshScheduler | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private refreshIntervalMs: number;
  private currentAccessRoleId: number | null = null;
  private queryClient: QueryClient | null = null;
  private repo = new CompositeTimelogRepository();
  private localRepo = new LocalTimelogRepository();

  private constructor(intervalMs = 5 * 60 * 1000) {
    this.refreshIntervalMs = intervalMs;
  }

  static getInstance(): TimelogRefreshScheduler {
    if (!TimelogRefreshScheduler.instance) {
      TimelogRefreshScheduler.instance = new TimelogRefreshScheduler();
    }
    return TimelogRefreshScheduler.instance;
  }

  /**
   * Start periodic background refresh for the given access role.
   *
   * Clears any previously running interval first, so calling `start()` while
   * already running (e.g., on profile switch) is safe and idempotent.
   * Each tick calls `refresh()`, which:
   *   1. Reads `cachedRanges.lastFetchedAt` from Dexie.
   *   2. Fetches timelogs incrementally since that timestamp.
   *   3. Invalidates the React Query `['timelogs']` cache if new data arrived.
   *   4. Checks storage quota and prunes oldest records when >90% full.
   *
   * Call `stop()` on profile switch or logout to prevent stale syncs and memory leaks.
   *
   * @param accessRoleId - The profile's access role to sync timelogs for.
   * @param queryClient  - React Query client used to invalidate `['timelogs']` queries.
   */
  start(accessRoleId: number, queryClient: QueryClient): void {
    this.stop(); // Stop any existing interval
    
    this.currentAccessRoleId = accessRoleId;
    this.queryClient = queryClient;

    this.intervalId = setInterval(() => {
      void this.refresh();
    }, this.refreshIntervalMs);
  }

  /**
   * Stop the refresh scheduler and release all held resources.
   *
   * Clears the `setInterval` timer and nulls out `currentAccessRoleId` and
   * `queryClient` so no stale references remain. Safe to call when no scheduler
   * is running (no-op in that case).
   *
   * Must be called on profile switch and on application logout.
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.currentAccessRoleId = null;
    this.queryClient = null;
  }

  /**
   * Force an immediate sync (for manual refresh or testing).
   */
  async forceSync(): Promise<void> {
    await this.refresh();
  }

  /**
   * Incremental refresh logic:
   * - Read lastFetchedAt from cachedRanges
   * - Fetch timelogs since that timestamp
   * - Invalidate React Query cache if new data exists
   * - Check storage quota and prune oldest records if above threshold
   *
   * T2.7: Incremental fetch since lastFetchedAt.
   * T2.8: Cache invalidation on new data.
   * T2.12/T4.4: Storage monitoring and auto-prune.
   */
  private async refresh(): Promise<void> {
    if (!this.currentAccessRoleId || !this.queryClient) return;

    try {
      const cachedRange = await db.cachedRanges.get(this.currentAccessRoleId);

      if (!cachedRange?.lastFetchedAt) {
        return;
      }

      const sinceDate = new Date(cachedRange.lastFetchedAt).toISOString();
      const newData = await this.repo.fetchIncremental(this.currentAccessRoleId, sinceDate);

      if (newData.length > 0) {
        // T2.8: Invalidate React Query cache on new data
        this.queryClient.invalidateQueries({ queryKey: ['timelogs'] });
      }

      // T2.12/T4.4: Check storage quota and prune if necessary
      await this.checkAndPruneStorage();
    } catch (error) {
      console.warn('[TimelogRefreshScheduler] Refresh failed:', error);
    }
  }

  /**
   * Monitor storage quota and prune oldest timelogs when approaching the limit.
   * T2.12/T4.4: Storage management with quota monitoring.
   *
   * - Uses `navigator.storage.estimate()` (no-op if API is unavailable).
   * - Prunes when usage exceeds 90% of quota.
   * - Keeps the most recent PRUNE_KEEP_COUNT records.
   */
  private async checkAndPruneStorage(): Promise<void> {
    if (!('storage' in navigator) || !navigator.storage || !('estimate' in navigator.storage)) {
      return; // Storage Estimation API not available (older browsers)
    }

    try {
      const { usage = 0, quota = 1 } = await navigator.storage.estimate();
      const usageRatio = usage / quota;

      if (usageRatio > STORAGE_PRUNE_THRESHOLD) {
        console.warn(
          `[TimelogRefreshScheduler] Storage at ${(usageRatio * 100).toFixed(1)}% — pruning oldest timelogs`,
        );
        await this.localRepo.pruneOldest(PRUNE_KEEP_COUNT);
      }
    } catch (error) {
      // Non-critical — log and continue
      console.warn('[TimelogRefreshScheduler] Storage estimate failed:', error);
    }
  }
}
